import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { uploadInstitutionAiAttachment } from '../services/institutions.js';
import { isCancelledStepUp, readableError } from '../actionErrors.js';

export const AI_IMAGE_TYPES = ['image/png', 'image/jpeg'];
export const AI_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
];
export const AI_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.pdf,.docx,.xlsx,.csv,.txt';
export const MAX_AI_ATTACHMENTS = 10;
export const MAX_AI_ATTACHMENT_BYTES = 5 * 1024 * 1024;

let sequence = 0;

const revokeOwnedPreview = (chip) => {
  if (chip?.file && chip.previewUrl && typeof URL !== 'undefined') {
    URL.revokeObjectURL(chip.previewUrl);
  }
};

const normalizeStored = (attachment, index) => {
  const isImage = attachment.isImage || attachment.kind === 'image';
  return {
    ...attachment,
    localId: attachment.localId || `stored-${attachment.fileId || index}`,
    name: attachment.name || attachment.originalFilename || (isImage ? 'Image' : 'File'),
    isImage,
    uploading: false,
    progress: 1,
    error: null,
    file: null,
    previewUrl: attachment.previewUrl || null,
  };
};

export default function useAiAttachments({ upload = uploadInstitutionAiAttachment } = {}) {
  const [chips, setChips] = useState([]);
  const [notice, setNotice] = useState(null);
  const chipsRef = useRef(chips);
  chipsRef.current = chips;

  useEffect(() => {
    return () => { chipsRef.current.forEach(revokeOwnedPreview); };
  }, []);

  const uploadOne = useCallback((localId, file) => {
    upload(file, {
      onProgress: (progress) => {
        setChips((current) => {
          return current.map((chip) => { return chip.localId === localId ? { ...chip, progress } : chip; });
        });
      },
    }).then((result) => {
      setChips((current) => {
        return current.map((chip) => {
          if (chip.localId !== localId) { return chip; }
          return {
            ...chip,
            fileId: result.fileId,
            mimeType: result.mimeType || chip.mimeType,
            originalFilename: result.originalFilename || chip.name,
            sizeBytes: result.sizeBytes ?? chip.sizeBytes,
            isImage: Boolean(result.isImage),
            truncated: Boolean(result.truncated),
            uploading: false,
            progress: 1,
          };
        });
      });
    }).catch((error) => {
      setChips((current) => {
        return current.map((chip) => {
          return chip.localId === localId
            ? { ...chip, uploading: false, error: isCancelledStepUp(error) ? 'Upload cancelled' : readableError(error, 'Upload failed') }
            : chip;
        });
      });
    });
  }, [upload]);

  const add = useCallback((fileList) => {
    const selected = Array.from(fileList || []);
    if (!selected.length) { return; }
    const allowedTypes = [...AI_IMAGE_TYPES, ...AI_DOCUMENT_TYPES];
    const accepted = [];
    const skipped = [];
    const pendingUploads = [];
    let available = MAX_AI_ATTACHMENTS - chipsRef.current.length;
    selected.forEach((file) => {
      if (available <= 0) {
        skipped.push(`${file.name} (maximum ${MAX_AI_ATTACHMENTS} files)`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        skipped.push(`${file.name} (unsupported type)`);
        return;
      }
      available -= 1;
      sequence += 1;
      const localId = `institution-ai-attachment-${Date.now()}-${sequence}`;
      const isImage = AI_IMAGE_TYPES.includes(file.type);
      if (file.size > MAX_AI_ATTACHMENT_BYTES) {
        accepted.push({
          localId,
          file,
          name: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          isImage,
          previewUrl: null,
          uploading: false,
          progress: 0,
          error: 'Exceeds 5MB limit',
          fileId: null,
        });
        return;
      }
      const chip = {
        localId,
        file,
        name: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        isImage,
        previewUrl: isImage && typeof URL !== 'undefined' ? URL.createObjectURL(file) : null,
        uploading: true,
        progress: 0,
        error: null,
        fileId: null,
      };
      accepted.push(chip);
      pendingUploads.push({ localId, file });
    });
    setNotice(skipped.length ? `Skipped: ${skipped.join(', ')}` : null);
    if (!accepted.length) { return; }
    setChips((current) => { return [...current, ...accepted]; });
    pendingUploads.forEach(({ localId, file }) => { uploadOne(localId, file); });
  }, [uploadOne]);

  const remove = useCallback((localId) => {
    setChips((current) => {
      const target = current.find((chip) => { return chip.localId === localId; });
      revokeOwnedPreview(target);
      return current.filter((chip) => { return chip.localId !== localId; });
    });
  }, []);

  const reset = useCallback((initial = []) => {
    chipsRef.current.forEach(revokeOwnedPreview);
    setNotice(null);
    setChips(initial.map(normalizeStored));
  }, []);

  const clear = useCallback(() => { reset([]); }, [reset]);

  const resolved = useCallback(() => {
    return chipsRef.current
      .filter((chip) => { return chip.fileId && !chip.error && !chip.uploading; })
      .map((chip) => {
        return {
          fileId: chip.fileId,
          kind: chip.isImage ? 'image' : 'document',
          mimeType: chip.mimeType,
          originalFilename: chip.originalFilename || chip.name,
          sizeBytes: chip.sizeBytes,
          truncated: Boolean(chip.truncated),
        };
      });
  }, []);

  return {
    chips,
    notice,
    setNotice,
    add,
    remove,
    reset,
    clear,
    resolved,
    uploadsPending: chips.some((chip) => { return chip.uploading; }),
  };
}
